import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Modal, message } from 'antd';
import { useTranslation } from 'react-i18next';
import { useQuery, useLazyQuery, useMutation } from '@apollo/client';
import { ReviewPoolItem } from '../types/reviewPool';
import { PickupSiteFormValues, PickupSiteFormMode, LinkStatus } from '../types/pickupSite';
import { GET_ALL_CHANNEL_LITE, allChannelLiteResponse } from '../../../../graphql/queries/getChannelAll';
import {
  GET_COOPERATION_WAREHOUSES_LISTING,
  CooperationWarehouseFiltersTypeInput,
} from '../../cooperation-warehouse/graphql/queries/getCooperationWarehouses';
import { GET_COOPERATION_WAREHOUSE_DETAIL } from '../../cooperation-warehouse/graphql/queries/getCooperationWarehouses';
import { PickupSiteFormBody, PickupSiteFormBodyRef, parseFullAddress, SiteAddressForSave } from './PickupSiteFormDrawer';
import { buildRowsFromCandidates, AbDimensionRow } from '../utils/abDimensions';
import { mapCwToForm } from '../utils/cwFormMapper';
import { buildSuggestedSiteName } from '../utils/siteName';
import {
  CREATE_DRAFT_COOPERATION_WAREHOUSE,
  CreateDraftCooperationWarehouseResponse,
} from '../graphql/mutations/draftCooperationWarehouse';
import { MODAL_HEADER_STYLE, MODAL_FOOTER_STYLE, modalBodyStyle } from '../utils/modalChrome';
import {
  GET_AB_DIMENSION_CANDIDATES,
  GetAbDimensionCandidatesResponse,
  AbDimensionCandidate,
} from '../graphql/queries/getAbDimensionCandidates';
// 2.2 需求点2：站点作为 A 时换这组取数，响应结构与上面完全一致
import {
  GET_STATION_AB_DIMENSION_CANDIDATES,
  GetStationAbDimensionCandidatesResponse,
  PREVIEW_STATION_AB_RETIRE,
  PreviewStationAbRetireData,
} from '../graphql/queries/stationAbMappingAsSource';
import {
  PREVIEW_AB_RETIRE,
  PreviewAbRetireData,
  AbRetirePreview,
  AbRetireBlockedReason,
  SILENT_BLOCKED_REASONS,
} from '../graphql/queries/previewAbRetire';

export interface AbMappingSubmit {
  targetSiteId: number;
  /** 选中的映射维度（每组=联系人+联系方式+渠道，可多组映射到同一实际站点 B） */
  mappings: { contactName?: string; contactPhone?: string; channelId?: number }[];
  fullySelected: boolean;
}

/**
 * 本向导的 **A 侧来源**（2.2 需求点2）。
 *
 * <p>2.1 时本向导只能从审核池条目发起，故直接收 `ReviewPoolItem`。2.2 要求**站点编辑时也能发起**
 * （把当前站点整体映射走），此时没有条目 —— 但向导实际只用到 5 个字段，故收窄成本接口。
 *
 * <p><b>交互、步骤、文案一个像素都没动</b>，这正是 PRD「交互逻辑：同现有的存量站点的AB映射」要求的。
 * 两条来源只在**三处取数**上分叉（维度候选 / 退役预览 / 新建 B 的草稿 sourceKey），见 `isStation` 的用处。
 */
export interface AbWizardSource {
  /** 'reviewPool' = 从审核池条目发起（2.1 既有）；'station' = 从站点编辑发起（2.2 需求点2） */
  kind: 'reviewPool' | 'station';
  /** 业务 id：条目 id 或 A 站点 id。取数与草稿 sourceKey 都用它 */
  id: number;
  /** A 所属 hub：向导要在同 hub 内搜 B 站点、建草稿，**没有它整个向导跑不起来**，故必填 */
  hubId: number;
  /**
   * 自指判据①用的站点 id：审核池来源 = 条目关联的站点（可空，系统进单条目没有）；
   * 站点来源 = 站点自己。与后端 `selfByStationId` 同口径
   */
  selfStationId?: number | null;
  /** 自指判据②用的命中键 */
  coreHash?: string | null;
  /** 向导里展示的 A 地址 */
  displayAddress: string;
}

interface Props {
  open: boolean;
  source: AbWizardSource;
  onClose: () => void;
  onSubmit?: (payload: AbMappingSubmit) => void;
  /**
   * 从「补录实际上门地址」弹窗带入的 B 地址（已过 standardizePickupAddress，与建站同口径）。
   *
   * <p>有值时自动跑一次 Step1 站点搜索：**恰好命中 1 个可用站点**才自动进 Step2；0 个或多个停在
   * Step1 让运营自己选。不直接 setStep(1) 是为了让「上一步」回到一个有数据的 Step1，
   * 且映射维度（联系人×渠道）仍在 Step1 有勾选位。
   */
  prefill?: SiteAddressForSave;
}

interface MatchedSite {
  id: number;
  name: string;
  address: string;
  /** 站点建联状态；`LinkStatus.Unreachable` 的站点不可作为 B（见 isUnreachableSite） */
  linkStatus?: number | null;
}

/**
 * 「确认无法建联」站点不能作为 AB 的 B 点（实际上门点）。
 *
 * 该状态的站点其预约单是静默创建的：不进预约单列表、不参与排线、不进揽收任务、运营不可见
 * （api-server `notUnreachableStation()` / pickup-service `PickupAppointmentMapper` 三处 link_status=5 排除）。
 * 一旦把 A 映射过去，A 的订单会被路由到该站点建出永远看不见也不会被揽收的预约单，
 * 同时审核池条目被标记完成、从待办消失 —— 比不做映射更糟（不映射至少会持续卡单提醒运营）。
 *
 * 其余状态（待上门/上门中/揽收成功待建联/揽收失败待建联/已建联）预约单均正常参与排线，放行。
 */
const isUnreachableSite = (s: { linkStatus?: number | null }) => s.linkStatus === LinkStatus.Unreachable;

/**
 * AB 地址映射两步向导（系统进单 / BD 录入 · 已联系到），样式严格对齐 PRD 原型（内联 style，与合并向导同视觉语言）。
 * 语义（A→B）：当前下单地址(A) 映射到实际上门站点(B)。映射后 A 不再建站/建单，命中 A 三要素(coreHash+联系方式+渠道)的
 * 下单前置路由到 B 建单。粒度=地址+联系方式+渠道，支持部分映射。
 * Step1：勾选映射维度(联系人×渠道) + 输入实际上门地址搜索同址站点；Step2：带入高亮块 + 内嵌 B 站点表单(已有=只读确认 / 无匹配=新建)。
 */
const AbMappingWizard: React.FC<Props> = ({ open, source, onClose, onSubmit, prefill }) => {
  /** 两条来源只在三处取数上分叉（维度候选 / 退役预览 / 草稿 sourceKey），其余逻辑完全共用 */
  const isStation = source.kind === 'station';
  const { t } = useTranslation();
  const aw = (k: string) => t(`pickup.addressReviewPool.abWizard.${k}`) as string;
  const [step, setStep] = useState(0);
  const [selectedDimKeys, setSelectedDimKeys] = useState<string[]>([]);
  /**
   * 实际上门地址（B）搜索关键字。
   *
   * <p>本步**不做地址标准化**：改用「站点管理」同一套地址模糊筛选（对 address_line_1/2 做 LIKE
   * 并 OR 关联地址），纯文本匹配、不碰 core_hash。此前按 hash 精确相等匹配，存量站点因 core_hash
   * 口径漂移一律搜不到，运营会误以为没有同址站点而重复建站。
   * 标准化推迟到 Step2 由运营对 B 站点地址显式执行。
   */
  const [addrKeyword, setAddrKeyword] = useState('');
  const [searchedSites, setSearchedSites] = useState<MatchedSite[] | null>(null);
  const [targetId, setTargetId] = useState<number | undefined>();
  // 搜索成功后落地的实际上门地址四段（新建 B 站点用作预填）
  const [parsedAddr, setParsedAddr] = useState<SiteAddressForSave | undefined>();
  // 选中已有 B 时加载其详情 → 映射为表单值（Step2 只读确认）
  const [targetForm, setTargetForm] = useState<{ values: Partial<PickupSiteFormValues>; address: SiteAddressForSave } | undefined>();
  const bodyRef = useRef<PickupSiteFormBodyRef>(null);
  // 新建 B：草稿预占真实 cw.id（供表单展示 + 保存转正；否则 saveCooperationWarehouse 成功不回传 id → cwId 空 → AB 写不进）
  const [draftId, setDraftId] = useState<number | undefined>();
  const [createDraft] = useMutation<
    CreateDraftCooperationWarehouseResponse,
    { hubId: number; sourceKey: string }
  >(CREATE_DRAFT_COOPERATION_WAREHOUSE);

  const [runSearch, { loading: searchLoading }] = useLazyQuery<
    { getCooperationWarehouseListing: { content: any[] } },
    { filter: CooperationWarehouseFiltersTypeInput; pageRequest: { page: number; size: number } }
  >(GET_COOPERATION_WAREHOUSES_LISTING, { fetchPolicy: 'network-only' });
  const [loadDetail] = useLazyQuery<{ getCooperationWarehouseById: any }>(GET_COOPERATION_WAREHOUSE_DETAIL, {
    fetchPolicy: 'network-only',
  });

  // 退役预览：提交前问一次「会不会一并作废站点」。network-only —— 站点的关联地址/联系人/
  // 预约单随时在变，缓存命中会拿到过期结论，而这个结论直接决定要不要拦运营。
  // 退役预览：两条来源的**响应结构完全一致**（后端同一套实现，只是 A 侧来源不同），
  // 故只换查询文档与变量名，下游判定逻辑一行不用改。
  const [loadPoolRetirePreview] = useLazyQuery<
    PreviewAbRetireData,
    { reviewPoolId: number; targetBCooperationWarehouseId: number; fullySelected: boolean }
  >(PREVIEW_AB_RETIRE, { fetchPolicy: 'network-only' });
  const [loadStationRetirePreview] = useLazyQuery<
    PreviewStationAbRetireData,
    { cooperationWarehouseId: number; targetBCooperationWarehouseId: number; fullySelected: boolean }
  >(PREVIEW_STATION_AB_RETIRE, { fetchPolicy: 'network-only' });
  const { data: channelData } = useQuery<allChannelLiteResponse>(GET_ALL_CHANNEL_LITE);
  const channelNameById = useMemo(
    () => new Map((channelData?.getAllChannelsLite ?? []).map((c) => [Number(c.id), c.channelName])),
    [channelData],
  );

  /**
   * AB 可选维度 = 该地址下**真实有订单**的（联系人+联系方式+渠道）三元组，由后端给。
   *
   * 不再用条目快照或站点档案：命中比的是 opi 的这三列，档案可被运营手改、快照永久缺维度，
   * 两头都会让运营「选了白选」或「想选选不到」。后端判整站作废用的是同一份列表 ⇒ 分母不会漂移。
   * 详见 graphql/queries/getAbDimensionCandidates.ts。
   */
  const [dimCandidates, setDimCandidates] = useState<AbDimensionCandidate[]>([]);
  const [dimLoading, setDimLoading] = useState(false);
  const [loadPoolDimCandidates] = useLazyQuery<GetAbDimensionCandidatesResponse>(
    GET_AB_DIMENSION_CANDIDATES,
    { fetchPolicy: 'network-only' },
  );
  const [loadStationDimCandidates] = useLazyQuery<GetStationAbDimensionCandidatesResponse>(
    GET_STATION_AB_DIMENSION_CANDIDATES,
    { fetchPolicy: 'network-only' },
  );

  const dimensionRows = useMemo(() => buildRowsFromCandidates(dimCandidates), [dimCandidates]);

  useEffect(() => {
    if (open) {
      setStep(0);
      // 选中态清空后由下面的「默认全选」effect 填充 —— 那里要等 A 站点维度到货才知道全集是什么。
      // effect 在同一次 commit 内按声明顺序执行，本 effect 必须留在它之前，位置不可下移。
      setSelectedDimKeys([]);
      setDimCandidates([]);
      // prefill 的 addressLine1 已含 dock（core_hash 口径），直接作为搜索关键字与新建 B 的预填
      setAddrKeyword(prefill?.addressLine1 ?? '');
      setSearchedSites(null);
      setTargetId(undefined);
      // standardized 仍置 false：Step2 新建 B 时要求运营对 B 地址显式再标准化一次，
      // 与手工进入向导的行为一致，避免带着"别处算的结果"直接落库
      setParsedAddr(prefill ? { ...prefill, standardized: false } : undefined);
      setTargetForm(undefined);
      setDraftId(undefined);
    }
  // 依赖取 id/kind 而不是整个 source 对象：站点入口的 source 由父组件内联构造，
  // 每次 render 都是新引用，依赖对象会让本 effect 每帧重跑
  }, [open, source.id, source.kind, prefill]);

  // 取可选维度。按条目取（后端内部并上"卡在本条目的单"与"站点近30天的单"），与后端分母同源。
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setDimLoading(true);
    void (async () => {
      try {
        // 两条来源的**行结构完全一致**，只是 GraphQL 字段名不同，取出来后下游一视同仁
        const rows = isStation
          ? (await loadStationDimCandidates({ variables: { cooperationWarehouseId: source.id } }))
              .data?.getStationAbDimensionCandidates
          : (await loadPoolDimCandidates({ variables: { reviewPoolId: source.id } }))
              .data?.getAbDimensionCandidates;
        if (!cancelled) setDimCandidates(rows ?? []);
      } catch {
        // 取不到就是空列表：宁可让运营看到"无可映射维度"并去反馈，也不要退回快照/档案
        // 那两个源会让他选出永远命不中的死映射（比看不到更糟，因为他会以为处理完了）。
        if (!cancelled) setDimCandidates([]);
      } finally {
        if (!cancelled) setDimLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, source.id, isStation, loadPoolDimCandidates, loadStationDimCandidates]);

  /**
   * 默认全选。**必须声明在上面两个 effect 之后**（同 commit 内按声明顺序执行）。
   *
   * 用 ref 记「已经呈现过的行」而不是无脑全选：A 站点维度是异步到货的，行集会从"条目快照"
   * 扩张成"快照 ∪ 站点实际"。无脑全选会把运营在这中间取消勾选的行重新勾上；
   * 只补 fresh 则新增行默认选中、已有的取消保持取消。
   */
  const offeredDimKeysRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (!open) {
      offeredDimKeysRef.current = new Set();
      return;
    }
    const all = dimensionRows.map((r) => r.key);
    // 已映射的不默认勾选：重复提交会插入重复 active 行（deactivateOthersByDimension 对"同维度同 B"
    // 不失效）。运营想改目标站点时仍可手动勾上。
    const fresh = dimensionRows
      .filter((r) => r.mappedTo == null && !offeredDimKeysRef.current.has(r.key))
      .map((r) => r.key);
    // 已映射的也要记进 offered，否则下次 effect 会把它当"新行"补勾上
    const newlyOffered = all.filter((k) => !offeredDimKeysRef.current.has(k));
    if (newlyOffered.length === 0) return;
    all.forEach((k) => offeredDimKeysRef.current.add(k));
    if (fresh.length > 0) setSelectedDimKeys((prev) => [...prev, ...fresh]);
  }, [open, dimensionRows]);

  // 放弃新建 B 时不删草稿占位：运营可能已拿该 id 建了微信群，删掉会导致重进换 id、群名错位；
  // 保留则重进复用同一 id（后端按 sourceKey + status=DRAFT 复用），超 24h 未转正由清理任务物理删。
  const handleClose = () => {
    setDraftId(undefined);
    onClose();
  };
  const handleBack = () => {
    setDraftId(undefined);
    setTargetForm(undefined);
    setStep(0);
  };

  const noResult = searchedSites !== null && searchedSites.length === 0;
  /**
   * 「全部维度都已映射走」——决定后端要不要整站作废。
   *
   * 已映射的行（mappedTo 非空）计为已覆盖，**不要求本次再勾一遍**：
   * 否则分批映射的最后一次永远达不成全选、退役永远不触发（而代码注释里写的设计意图正是分批）。
   * 与后端 allStationDimensionsMapped 同口径 —— 那边也是拿 active 映射集去比同一份候选列表。
   */
  const fullySelected =
    dimensionRows.length > 0 &&
    dimensionRows.every((r) => r.mappedTo != null || selectedDimKeys.includes(r.key));
  const selectedDims = dimensionRows.filter((r) => selectedDimKeys.includes(r.key));
  const firstSelectedContactName = selectedDims[0]?.contactName;

  /**
   * 新建 B 站点的表单预填。名称按与「新地址审核 → 创建新站点」完全相同的规则自动生成，可编辑。
   *
   * 取数用**标准化后的 B 地址**（parsedAddr）与选中维度的联系人：B 是实际上门点、也是这个站点
   * 自身所在地；若沿用 A（下单地址）拼名字，站点名会指向另一个城市/邮编，运营按名字找站点会找错。
   *
   * 必须 useMemo：PickupSiteFormBody 的播种 effect 依赖 initialValues 的引用，
   * 每次渲染传新对象字面量会触发 form.resetFields()，把运营已填的内容清掉。
   * 依赖项在 Step2 期间均不变（勾选维度在 Step1、表单内重新标准化不回写 parsedAddr），
   * 因此名称只在进入 Step2 时生成一次，不会覆盖运营手改的名字。
   */
  const newSiteInitialValues = useMemo<Partial<PickupSiteFormValues>>(
    () => ({
      linkStatus: LinkStatus.Linked,
      name: buildSuggestedSiteName({
        contactName: firstSelectedContactName,
        // Step1 改成关键字搜索后 parsedAddr 只有 addressLine1（运营原始输入）、没有 zipcode，
        // 故生成的名称会少「邮编」那一段（buildSuggestedSiteName 会过滤空值）。
        // 不在 Step2 标准化后重算：那会覆盖运营手改的名字；名称仍含联系人+街道+站点ID，可辨识且唯一。
        addressLine1: parsedAddr?.addressLine1,
        zipcode: parsedAddr?.zipcode,
        cwId: draftId,
      }),
    }),
    [firstSelectedContactName, parsedAddr, draftId],
  );

  /**
   * 搜索 = 地址标准化 + 查同址站点。
   *
   * 标准化必须前置：此前直接把手输串按逗号切段就去查，四段既未归一、也没有 line2，
   * 与站点入库时的地址库口径不一致（coreHash 对不上 → 同址站点搜不到）；且 Step2 会把这个
   * 从未标准化的地址当成「已标准化」显示绿勾。现在改为与站点表单同一条标准化链路。
   */
  /**
   * 搜索同址站点：走「站点管理」的地址模糊筛选，纯文本 LIKE，不做标准化、不比 hash。
   *
   * <p>自指判定退化为**关键字与 A 地址的归一化字面比较**——本步已无标准化结果可比。
   * 这是弱判据：写法变体（Rd/Road）会漏判。⚠️ 向导路径的后端目前**没有**自指兜底
   * （`resolveAsAbMapping` 无校验，`saveStationAbMappings` 的 analysisHash 拦截只覆盖站点表单内的 AB 行），
   * 漏判会真的写出错误映射，需后续在 resolveAsAbMapping 补权威拦截。
   */
  /**
   * 按关键字搜同址站点并落地结果。抽出 keyword 入参是为了让 prefill 自动流程能立即使用
   * （setAddrKeyword 是异步的，同一轮里读 state 会拿到旧值）。
   *
   * @returns 命中的站点列表；自指或空关键字返回 null（调用方据此不再自动前进）
   */
  const runSiteSearch = async (rawKeyword: string, addrSeed?: SiteAddressForSave): Promise<MatchedSite[] | null> => {
    const keyword = rawKeyword.trim();
    // 空关键词直接不搜：输入框上方常驻提示已说明该输什么，再弹 toast 属重复打扰
    if (!keyword) return null;
    setTargetId(undefined);
    setSearchedSites(null);
    // 这里**刻意不做自指拦截**（产品定案 2026-08-25）：搜索只是"找地址对应的站点"。
    // 拿关键字与下单地址做字面比对既拦不准（Rd/Road 之类写法变体漏判），又会把
    // "同一条街不同门牌 / 不同 dock"这种合法场景一并拦掉，运营连有哪些候选都看不到。
    // 自指判定移到「选中站点 → 点下一步」，判据换成 hash，见 advanceToStep2。
    // Step2 新建 B 用的预填：手工搜索时只有运营输入的原始文本；从补录弹窗进来时带完整四段。
    // 两种情况 standardized 都显式置 false —— 进 Step2 必须显示「⚠ 请点击地址标准化」，
    // 不能沿用旧行为的假「已标准化」绿勾
    setParsedAddr(
      addrSeed
        ? { ...addrSeed, standardized: false }
        : { addressLine1: keyword, country: 'US', standardized: false },
    );

    const { data } = await runSearch({
      variables: {
        filter: { warehouseId: String(source.hubId), address: keyword, status: [1, 2] },
        pageRequest: { page: 1, size: 50 },
      },
    });
    const list = data?.getCooperationWarehouseListing?.content ?? [];
    const sites: MatchedSite[] = list.map((c: any) => ({
      id: Number(c.id),
      name: c.name ?? '',
      address: [c.addressLine1, c.city, c.state, c.zipcode].filter(Boolean).join(', '),
      linkStatus: c.linkStatus,
    }));
    setSearchedSites(sites);
    return sites;
  };

  const handleSearch = () => runSiteSearch(addrKeyword);

  /**
   * 自指判定：选中的 B 站点其实就是下单地址(A)自己所在的站点。
   *
   * <p>判据与后端 `resolveAsAbMapping` 的**前两条**逐字对齐（都是精确比对、零误判）：
   * 后端还有第三条（relate 表里是否已承接该地址，`existsHitableAddress`），前端拿不到 relate 数据、
   * 不做这条 —— 那种场景由后端权威拦截，代价只是运营走到最后一步才被拒。
   * <ol>
   *   <li>历史存量条目：目标 B 的 id == 条目关联的站点 id</li>
   *   <li>任意条目：目标 B 的 core_hash == 条目的 core_hash
   *       （系统进单条目没有关联站点，只能靠 hash）</li>
   * </ol>
   * 前后端判据必须一致，否则会出现「前端放行、后端拒绝」——运营走完两步才吃到报错。
   *
   * <p>为什么用 `core_hash` 而不是站点表单那条路的 `analysis_hash`：条目的 core_hash 与站点建站时
   * 算的**同源**（都是地址库原值），可以逐字节比；而站点表单里的 A 地址是运营手输、无 dock 输入，
   * core_hash 两边算不出一致值，那条路才只能退而用 analysis_hash。两处判据不同是有理由的，别对齐。
   *
   * <p>core_hash 任一为空时**不判**（返回 false）：空值比对会把"读不到"误判成"不同"或"相同"，
   * 而后端还有一道权威拦截，漏判在这里的代价只是运营多走一步。
   */
  const isSelfTargetSite = (tid: number, detail: { addressCoreHash?: string } | undefined) => {
    if (source.selfStationId != null && source.selfStationId === tid) {
      return true;
    }
    const bHash = detail?.addressCoreHash;
    return !!bHash && !!source.coreHash && bHash === source.coreHash;
  };

  /**
   * 进 Step2。tid / dimCount 显式入参，供 prefill 自动流程在 state 尚未落地时直接驱动。
   */
  const advanceToStep2 = async (tid: number | undefined, dimCount: number) => {
    if (dimCount === 0) {
      message.warning(aw('dimHint'));
      return;
    }
    if (tid != null) {
      // 选中已有 B → 加载详情映射为表单值（只读确认）
      const { data } = await loadDetail({ variables: { id: tid } });
      const v = data?.getCooperationWarehouseById;
      // 自指拦截（产品定案 2026-08-25：搜索后选站点、点下一步才拦，判据用 hash）。
      // 放在这里而不是搜索时：判据所需的 core_hash 只有站点详情才返回，列表接口没有
      // —— 也就是说"选中站点之后"本来就是唯一能精确判定的时点。
      // 复用这次 loadDetail，零额外请求。
      if (isSelfTargetSite(tid, v)) {
        message.error(aw('sameAsOrderAddress'));
        return;
      }
      // 二次校验：列表已置灰，但站点状态可能在搜索之后被别人改成「确认无法建联」，
      // 以详情接口这份更新的数据为准再拦一次（后端 resolveAsAbMapping 还有一道兜底）
      if (v && isUnreachableSite({ linkStatus: v.linkStatus })) {
        message.error(aw('unreachableTargetBlocked'));
        return;
      }
      setTargetForm(v ? mapCwToForm(v) : undefined);
    } else {
      // 无匹配 → 新建 B：草稿预占真实 cw.id（转正/保存拿得到 id，才能写 AB 映射）
      // sourceKey 用 :ab 后缀与同条目的「新建站点」(:main) 区分——两者是不同草稿，不可共用一个 id
      setTargetForm(undefined);
      try {
        const resp = await createDraft({
          // sourceKey 必须按来源区分：复用同一个 key 会让站点入口拿到条目那边的草稿 id
          variables: { hubId: source.hubId, sourceKey: `${isStation ? 'station' : 'review'}:${source.id}:ab` },
        });
        const id = resp.data?.createDraftCooperationWarehouse;
        if (id == null) {
          message.error(t('pickup.addressReviewPool.siteForm.saveFailed') as string);
          return;
        }
        setDraftId(Number(id));
      } catch {
        message.error(t('pickup.addressReviewPool.siteForm.saveFailed') as string);
        return;
      }
    }
    setStep(1);
  };

  const handleNext = () => advanceToStep2(targetId, selectedDims.length);

  /**
   * prefill 自动流程：带着补录的 B 地址进来 → 自动搜同址站点 → 恰好命中 1 个可用站点就直接进 Step2。
   *
   * 0 个（要新建 B）或多个（要运营挑）时停在 Step1，此时关键字与四段已预填好，运营只需点选。
   * 命中的唯一站点若是「确认无法建联」也不自动前进——它不能作 B，让运营在 Step1 看到置灰原因。
   * ref 保证一次打开只自动跑一次，否则 state 更新会把 effect 重新触发成死循环。
   */
  const autoRanRef = useRef(false);
  useEffect(() => {
    if (!open) {
      autoRanRef.current = false;
      return;
    }
    if (!prefill?.addressLine1 || autoRanRef.current) return;
    autoRanRef.current = true;
    void (async () => {
      const sites = await runSiteSearch(prefill.addressLine1 as string, prefill);
      if (!sites || sites.length !== 1 || isUnreachableSite(sites[0])) return;
      setTargetId(sites[0].id);
      await advanceToStep2(sites[0].id, dimensionRows.length);
    })();
    // runSiteSearch / advanceToStep2 每次渲染都是新引用，列进依赖会重复触发；由 autoRanRef 兜住幂等
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, prefill, dimensionRows.length]);

  /**
   * 提交前问一次退役预览，按 blockedReason 分三层处理。返回 true=可以提交。
   *
   * **「被挡」不等于「不能提交」** —— 被挡的语义是「AB 映射照做，只是不整站作废」，
   * 而运营的意图往往就是只做映射。一律拦死会把合法操作也堵住，所以分三层：
   *
   * | blockedReason | 处理 | 理由 |
   * |---|---|---|
   * | DISABLED / NOT_APPLICABLE / NOT_FULLY_SELECTED | 静默提交 | 都是正常场景：功能没开是当前默认态、系统进单条目本就不退役、只勾部分是产品设计的分批路径。弹窗只会训练运营无脑点确定 |
   * | HAS_IN_PROGRESS_APPOINTMENT | **阻断** | 产品定案：站点还有司机在路上，要求先取消或延后 |
   * | 其余（自指/他人B端/联系人读不出/长期揽收/四道量级） | 二次确认 | 告知「只写映射、不作废站点，原因 X」，运营确认后仍可继续 |
   *
   * 查询失败 → **放行**（返回 true）。预览是增强信息，不能因为它挂了就让运营做不了 AB 映射；
   * 后端 evaluateAbRetire 在写入侧还会再判一次，真正的决定权在后端。
   */
  const confirmRetireImpact = async (targetSiteId: number, fully: boolean): Promise<boolean> => {
    let p: AbRetirePreview | undefined;
    try {
      if (isStation) {
        const { data } = await loadStationRetirePreview({
          variables: {
            cooperationWarehouseId: source.id,
            targetBCooperationWarehouseId: targetSiteId,
            fullySelected: fully,
          },
        });
        p = data?.previewStationAbRetire;
      } else {
        const { data } = await loadPoolRetirePreview({
          variables: {
            reviewPoolId: source.id,
            targetBCooperationWarehouseId: targetSiteId,
            fullySelected: fully,
          },
        });
        p = data?.previewAbRetire;
      }
    } catch {
      return true;
    }
    if (!p || !p.applicable || p.willRetire) {
      return true;
    }
    const reason = p.blockedReason ?? '';
    if (!reason || SILENT_BLOCKED_REASONS.includes(reason)) {
      return true;
    }

    // 阻断：站点存在进行中预约单
    if (reason === AbRetireBlockedReason.HasInProgressAppointment) {
      const list = (p.blockingAppointments ?? [])
        .map((a) => `${a.expectedPickupDate ?? '-'} ${a.appointmentNumber ?? '-'}`)
        .join('、');
      Modal.error({
        title: aw('retireBlockedTitle'),
        content: aw('retireBlockedInProgress').replace('{list}', list),
        okText: t('common.confirm', '确定') as string,
      });
      return false;
    }

    // 二次确认：只写映射、不作废站点
    const detail =
      reason === AbRetireBlockedReason.StandingPickupStation ? aw('retireReasonStandingPickup')
      : reason === AbRetireBlockedReason.MultiCity ? aw('retireReasonMultiCity').replace('{n}', String(p.cityCount)).replace('{max}', String(p.maxCityCount))
      : reason === AbRetireBlockedReason.TooManyAddresses ? aw('retireReasonTooManyAddresses').replace('{n}', String(p.addressCount)).replace('{max}', String(p.maxAddressCount))
      : reason === AbRetireBlockedReason.TooManyContacts ? aw('retireReasonTooManyContacts').replace('{n}', String(p.contactCount)).replace('{max}', String(p.maxContactCount))
      : reason === AbRetireBlockedReason.TooManyChannels ? aw('retireReasonTooManyChannels').replace('{n}', String(p.channelCount)).replace('{max}', String(p.maxChannelCount))
      : reason === AbRetireBlockedReason.TooManyRows ? aw('retireReasonTooManyRows').replace('{n}', String(p.estimatedRows)).replace('{max}', String(p.maxEstimatedRows))
      : reason === AbRetireBlockedReason.SelfTarget ? aw('retireReasonSelfTarget')
      : reason === AbRetireBlockedReason.IsAbTarget ? aw('retireReasonIsAbTarget')
      : reason === AbRetireBlockedReason.ContactUnparseable ? aw('retireReasonContactUnparseable')
      // 后端新增了原因但前端还没跟上时，直接把原因码摊出来 —— 比显示空白好排查
      : reason;
    return await new Promise<boolean>((resolve) => {
      Modal.confirm({
        title: aw('retireSkippedTitle'),
        content: `${aw('retireSkippedHint')}\n${detail}`,
        okText: t('common.confirm', '确定') as string,
        cancelText: t('common.cancel', '取消') as string,
        onOk: () => resolve(true),
        onCancel: () => resolve(false),
      });
    });
  };

  const doAbSubmit = async (targetSiteId: number, skipRetireConfirm = false) => {
    // 退役影响确认必须在 onSubmit 之前：一旦提交，站点该不该退役已经由后端决定完了。
    // skipRetireConfirm：新建 B 路径已在建站前用 draftId 确认过，不再弹第二次
    if (!skipRetireConfirm && !(await confirmRetireImpact(targetSiteId, fullySelected))) {
      return;
    }
    const mappings = selectedDims.map((r) => ({
      contactName: r.contactName,
      contactPhone: r.contactPhone,
      channelId: r.channelId,
    }));
    onSubmit?.({ targetSiteId, mappings, fullySelected });
    onClose();
  };

  const handleConfirm = async () => {
    if (targetId != null) {
      // 提交前最后一道：目标 B 若已「确认无法建联」则明确拦截（比后端拒绝后的笼统「请重试」清楚）。
      // 站点状态可能在选站之后被改；用最新详情再校验一次，避免运营到 Step2 提交才吃到后端兜底。
      const { data } = await loadDetail({ variables: { id: targetId } });
      const v = data?.getCooperationWarehouseById;
      if (v && isUnreachableSite({ linkStatus: v.linkStatus })) {
        message.error(aw('unreachableTargetBlocked'));
        return;
      }
      await doAbSubmit(targetId); // 已有 B：只读确认，直接写映射
    } else {
      // 新建 B：**必须在建站之前**做退役影响确认。
      // bodyRef.submit() 会把站点真正落库，落库后才在 onSaved 里走 doAbSubmit —— 若那时被
      // HAS_IN_PROGRESS_APPOINTMENT 拦下（或运营在二次确认里点了取消），站点已经建出来了，
      // 而 AB 映射没写、审核池条目还挂着，留下一个没人认领的空站点。
      //
      // 用 draftId 作 targetB 预检：退役判定看的是**源站点**（条目的 related_warehouse_id），
      // targetB 只参与自指判定，而新建的 B 不可能与源站点自指，结论与建站后一致。
      if (draftId != null && !(await confirmRetireImpact(draftId, fullySelected))) {
        return;
      }
      bodyRef.current?.submit(); // 通过预检才建站 → onSaved 回调里写映射
    }
  };

  // ===== 样式（与合并向导同视觉语言） =====
  const circle = (active: boolean): React.CSSProperties => ({
    width: 34,
    height: 34,
    borderRadius: '50%',
    background: active ? '#7c4bf0' : '#b7b2c4',
    color: '#fff',
    fontSize: 15,
    fontWeight: 800,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flex: '0 0 34px',
  });
  const greyBtn: React.CSSProperties = {
    border: '1px solid #b7b2c4',
    background: '#8a8698',
    color: '#fff',
    fontSize: 13,
    fontWeight: 700,
    padding: '9px 24px',
    borderRadius: 9,
    cursor: 'pointer',
  };
  const purpleBtn = (enabled: boolean): React.CSSProperties => ({
    border: 'none',
    background: enabled ? '#6d28d9' : '#c9bff0',
    color: '#fff',
    fontSize: 13,
    fontWeight: 700,
    padding: '9px 28px',
    borderRadius: 9,
    cursor: enabled ? 'pointer' : 'not-allowed',
  });
  const groupTitle: React.CSSProperties = { fontSize: 13, fontWeight: 800, color: '#3a3550', margin: '16px 0 8px' };
  const subHint: React.CSSProperties = { fontSize: 11.5, color: '#8a8698', marginBottom: 10 };
  // 带入高亮框（紫底，对齐原型）
  const biBox: React.CSSProperties = {
    flex: 1,
    border: '1px solid #d9cffa',
    background: '#f6f2ff',
    borderRadius: 7,
    padding: '9px 12px',
    fontSize: 12.5,
    color: '#4b3fa6',
    fontWeight: 700,
  };

  // 不再含 !selfRef：自指改为「点下一步时」判定并 toast 拦截，不再置灰按钮 ——
  // 置灰会让运营不知道为什么点不动（判据是 hash，界面上看不出两个地址"相同"）
  const canNext = selectedDims.length > 0 && (targetId != null || noResult);

  const footer = (
    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
      {step === 0 ? (
        <>
          <button onClick={handleClose} style={greyBtn}>
            {t('common.cancel', '取消') as string}
          </button>
          <button onClick={handleNext} disabled={!canNext} style={purpleBtn(canNext)}>
            {t('pickup.addressReviewPool.wizard.next') as string}
          </button>
        </>
      ) : (
        <>
          <button onClick={handleBack} style={greyBtn}>
            {t('pickup.addressReviewPool.wizard.prev') as string}
          </button>
          <button onClick={handleConfirm} style={purpleBtn(true)}>
            {t('common.confirm', '确定') as string}
          </button>
        </>
      )}
    </div>
  );

  return (
    <Modal
      open={open}
      width={1280}
      onCancel={handleClose}
      footer={footer}
      // header/footer 固定并各带分隔线，滚动只发生在两线之间（对齐 PRD 原型）
      styles={{
        header: MODAL_HEADER_STYLE,
        body: modalBodyStyle('72vh'),
        footer: MODAL_FOOTER_STYLE,
      }}
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ width: 5, height: 22, background: '#7c4bf0', borderRadius: 3, display: 'inline-block' }} />
          <span style={{ fontSize: 18, fontWeight: 800 }}>{aw('title')}</span>
        </div>
      }
    >
      <div style={{ fontSize: 12, color: '#6d28d9', paddingBottom: 14, borderBottom: '1px solid #efeaf7' }}>
        {aw('pickHint')}
      </div>

      {/* stepper */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 16, padding: '20px 0' }}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
          <span style={circle(true)}>1</span>
          <span style={{ fontSize: 12.5, fontWeight: 700, color: '#3a3550' }}>{aw('step1')}</span>
        </div>
        <span style={{ width: 120, height: 2, background: '#2a2636', marginBottom: 24 }} />
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
          <span style={circle(step === 1)}>2</span>
          <span style={{ fontSize: 12.5, fontWeight: 700, color: '#3a3550' }}>{aw('step2')}</span>
        </div>
      </div>

      <div style={{ borderTop: '1px solid #efeaf7', paddingTop: 6 }}>
        {step === 0 ? (
          /* ===== STEP1：当前下单地址 + 映射维度勾选 + 实际上门地址搜索 + 匹配站点勾选 ===== */
          <div>
            <div style={groupTitle}>{aw('currentOrderAddr')}</div>
            <div
              style={{
                border: '1px solid #d9d5e6',
                borderRadius: 8,
                padding: '10px 14px',
                fontSize: 13,
                textAlign: 'center',
                color: '#2a2636',
                marginBottom: 20,
              }}
            >
              {source.displayAddress}
            </div>

            <div style={{ fontSize: 13, fontWeight: 800, color: '#3a3550', marginBottom: 4 }}>{aw('dimTitle')}</div>
            <div style={subHint}>{aw('dimHint')}</div>
            <div style={{ border: '1px solid #e6e2f0', borderRadius: 10, overflow: 'hidden', marginBottom: 20 }}>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: '40px 1fr 1fr 1fr 120px',
                  fontSize: 12,
                  fontWeight: 700,
                  color: '#6b6778',
                  background: '#faf8ff',
                  padding: '10px 14px',
                }}
              >
                <span>
                  <input
                    type="checkbox"
                    checked={fullySelected}
                    onChange={(e) =>
                      setSelectedDimKeys(
                        e.target.checked
                          ? dimensionRows.filter((r) => r.mappedTo == null).map((r) => r.key)
                          : [],
                      )
                    }
                  />
                </span>
                <span>{aw('colContact')}</span>
                <span>{aw('colPhone')}</span>
                <span>{aw('colChannel')}</span>
                <span style={{ textAlign: 'right' }} />
              </div>
              {dimensionRows.map((r: AbDimensionRow) => {
                const checked = selectedDimKeys.includes(r.key);
                return (
                  <div
                    key={r.key}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '40px 1fr 1fr 1fr 120px',
                      fontSize: 12.5,
                      color: '#2a2636',
                      padding: '10px 14px',
                      borderTop: '1px solid #f0ecf8',
                      alignItems: 'center',
                    }}
                  >
                    <span>
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={(e) =>
                          setSelectedDimKeys((prev) =>
                            e.target.checked ? [...prev, r.key] : prev.filter((k) => k !== r.key),
                          )
                        }
                      />
                    </span>
                    <span>{r.contactName || '-'}</span>
                    <span>{[r.phoneCountryCode, r.contactPhone].filter(Boolean).join(' ')}</span>
                    <span>{channelNameById.get(r.channelId) ?? `#${r.channelId}`}</span>
                    {/* 单量是运营判断"这个维度该不该勾"的唯一客观依据；
                        0 表示后端走了兜底（站点近期无单、用条目快照推断），必须显式标注，
                        否则运营会以为它和其它行一样有真实订单支撑。 */}
                    <span style={{ textAlign: 'right' }}>
                      {r.mappedTo != null && (
                        <span style={{ color: '#16a34a', fontWeight: 700 }}>
                          {aw('alreadyMapped')}#{r.mappedTo}
                        </span>
                      )}
                    </span>
                  </div>
                );
              })}
              {dimensionRows.length === 0 && (
                <div style={{ padding: '14px', fontSize: 12.5, color: '#928da0', textAlign: 'center' }}>
                  {dimLoading ? aw('dimLoading') : aw('noDimension')}
                </div>
              )}
            </div>

            <div style={{ fontSize: 13, fontWeight: 800, color: '#3a3550', marginBottom: 4 }}>{aw('actualAddress')}</div>
            <div style={{ fontSize: 11.5, color: '#6d28d9', marginBottom: 10 }}>{aw('googleHint')}</div>
            {/* 本步只输地址关键字 + 搜索，不做标准化、不要 Dock/州/城市/邮编 ——
                走「站点管理」同一套地址模糊筛选。标准化在 Step2 对 B 站点地址执行。 */}
            <div style={{ display: 'flex', gap: 10, marginBottom: 18 }}>
              <input
                value={addrKeyword}
                placeholder={t('pickup.addressReviewPool.siteForm.streetPlaceholder') as string}
                onChange={(e) => setAddrKeyword(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleSearch();
                }}
                style={{
                  flex: 1,
                  border: '1px solid #d9d5e6',
                  borderRadius: 7,
                  padding: '8px 12px',
                  fontSize: 13,
                  outline: 'none',
                }}
              />
              <button onClick={handleSearch} disabled={searchLoading} style={purpleBtn(!searchLoading)}>
                {aw('search')}
              </button>
            </div>

            {searchedSites !== null && (
              <>
                <div style={subHint}>{aw('matchedHint')}</div>
                {noResult ? (
                  <div
                    style={{
                      border: '1px solid #f6e0cf',
                      background: '#fdf3e7',
                      borderRadius: 8,
                      padding: '10px 14px',
                      fontSize: 12.5,
                      color: '#b45309',
                    }}
                  >
                    {aw('noSearchResult')}
                  </div>
                ) : (
                  <div style={{ border: '1px solid #e6e2f0', borderRadius: 10, overflow: 'hidden' }}>
                    <div
                      style={{
                        display: 'grid',
                        gridTemplateColumns: '40px 1fr 1fr 2.4fr',
                        fontSize: 12,
                        fontWeight: 700,
                        color: '#6b6778',
                        background: '#faf8ff',
                        padding: '10px 14px',
                      }}
                    >
                      <span />
                      <span>{aw('colSiteId')}</span>
                      <span>{aw('colName')}</span>
                      <span>{aw('colAddress')}</span>
                    </div>
                    {searchedSites.map((s) => {
                      // 置灰不可选，并当场说明原因——不让运营选完到 Step2 才被拒
                      const disabled = isUnreachableSite(s);
                      return (
                        <div
                          key={s.id}
                          onClick={disabled ? undefined : () => setTargetId(s.id)}
                          style={{
                            display: 'grid',
                            gridTemplateColumns: '40px 1fr 1fr 2.4fr',
                            fontSize: 12.5,
                            color: disabled ? '#a8a4b5' : '#2a2636',
                            padding: '10px 14px',
                            borderTop: '1px solid #f0ecf8',
                            alignItems: 'center',
                            cursor: disabled ? 'not-allowed' : 'pointer',
                            background: disabled ? '#fafafa' : targetId === s.id ? '#f6f2ff' : undefined,
                          }}
                        >
                          <span>
                            <input
                              type="checkbox"
                              disabled={disabled}
                              checked={targetId === s.id}
                              onChange={() => setTargetId(s.id)}
                            />
                          </span>
                          <span style={{ fontWeight: 700 }}>{s.id}</span>
                          <span>{s.name || '-'}</span>
                          <span>
                            {s.address}
                            {disabled && (
                              <div style={{ fontSize: 11.5, color: '#b45309', marginTop: 2 }}>
                                ⚠️ {aw('unreachableSiteHint')}
                              </div>
                            )}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </>
            )}
          </div>
        ) : (
          /* ===== STEP2：带入高亮块（每个选中维度一块） + 内嵌 B 站点表单 ===== */
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '10px 0 10px' }}>
              <span style={{ fontSize: 13.5, fontWeight: 800, color: '#3a3550' }}>🔗 {aw('broughtInTitle')}</span>
              <span style={{ fontSize: 11.5, color: '#6d28d9' }}>{aw('broughtInHint')}</span>
            </div>
            {/* 地址(A)只显一次 */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
              <span style={{ fontSize: 11.5, color: '#8a8698', width: 60, flex: '0 0 60px' }}>{aw('addressLabel')}</span>
              <div style={biBox}>{source.displayAddress}</div>
            </div>
            {/* 每个选中维度一行：联系人 / 联系方式 / 客户渠道 */}
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1.3fr 1.3fr',
                gap: 10,
                fontSize: 11.5,
                color: '#8a8698',
                margin: '0 0 4px 70px',
              }}
            >
              <span>{aw('colContact')}</span>
              <span>{aw('colPhone')}</span>
              <span>{aw('customerChannel')}</span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginLeft: 70 }}>
              {selectedDims.map((r) => (
                <div key={r.key} style={{ display: 'grid', gridTemplateColumns: '1fr 1.3fr 1.3fr', gap: 10 }}>
                  {[
                    r.contactName || '-',
                    [r.phoneCountryCode, r.contactPhone].filter(Boolean).join(' '),
                    channelNameById.get(r.channelId) ?? `#${r.channelId}`,
                  ].map((val, i) => (
                    <div key={i} style={biBox}>
                      {val}
                    </div>
                  ))}
                </div>
              ))}
            </div>

            <div style={{ borderTop: '1px solid #efeaf7', marginTop: 14, paddingTop: 8 }}>
              <PickupSiteFormBody
                ref={bodyRef}
                open={step === 1}
                mode={(targetId != null ? 'view' : 'create') as PickupSiteFormMode}
                warehouseId={targetId != null ? undefined : source.hubId}
                cooperationWarehouseId={targetId ?? draftId}
                // 状态不可选、新建 B 固定「已建联」：AB 的 B 仓是客户指定的实际上门点，做映射时
                // 运营已与客户联系上，故不应出现「揽收成功待建联」这类前提不成立的状态（实测站点
                // 11569 曾被选成 2）。与「新地址审核·创建新站点」同样锁定状态、由场景推导取值。
                // 已建联会连带要求群ID+群名（requireGroup），符合「已建联=有群」口径。
                // targetId != null 是选已有 B 的只读确认分支，不改其原状态。
                statusEditable={false}
                initialValues={targetId != null ? targetForm?.values : newSiteInitialValues}
                addressForSave={targetId != null ? targetForm?.address : parsedAddr}
                onSaved={(cwId) => void doAbSubmit(cwId, true)}
              />
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default AbMappingWizard;
